import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    CircularProgress,
    Alert,
    Container,
    Box,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    LinearProgress,
    Fade,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CardHeader,
    Slide,
    Box as MUIBox,
    Toolbar,
    AppBar,
    useScrollTrigger,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Confetti from 'react-confetti';
import { useAuth } from './AuthContext';
import { userProgressService } from './userProgressService';
import ChatBot from './ChatBot';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ErrorIcon from '@mui/icons-material/Error';
import TopHeader from '../components/TopHeader';
import Header from '../components/Header';
import EnhancedHeader from './EnhancedHeader';
import ImageViewer from './ImageViewer';

const TestPage = () => {
    const { subscriptionId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const testId = new URLSearchParams(location.search).get('test');
    const [testData, setTestData] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [correctAnswer, setCorrectAnswer] = useState(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [testCompleted, setTestCompleted] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [score, setScore] = useState(0);
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const resizeObserverRef = useRef(null);
    const [correctlyAnsweredQuestions, setCorrectlyAnsweredQuestions] = useState(new Set());
    const [attemptedQuestions, setAttemptedQuestions] = useState(new Set());
    const [incorrectAnswers, setIncorrectAnswers] = useState(0);
    const [firstAttemptCorrect, setFirstAttemptCorrect] = useState(new Set());
    const [resumeData, setResumeData] = useState(null);
    const [missedQuestions, setMissedQuestions] = useState([]);
    const [isRetakingMissed, setIsRetakingMissed] = useState(false);
    const [userProgress, setUserProgress] = useState(null);
    const [watermarkText, setWatermarkText] = useState('');
    const [isWebchatOpen, setIsWebchatOpen] = useState(false);
    const [showHeader, setShowHeader] = useState(false);
    const [questionOrder, setQuestionOrder] = useState([]);
    const [questionMap, setQuestionMap] = useState(new Map());

    // Helper function to get full image URL
    const getImageUrl = (path) => {
        if (!path) return '';
        // If it's already a full URL, return it as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        // For relative paths, prepend the API URL
        return `${process.env.REACT_APP_API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    // Time tracking
    const timeTrackingRef = useRef({
        startTime: Date.now(),
        lastSyncTime: Date.now(),
        accumulatedTime: 0,
    });
    const timeTrackingIntervalRef = useRef(null);

    const action = new URLSearchParams(location.search).get('action');

    // Guard to prevent re-initialization when auth context refreshes (e.g., subscription refresh)
    const hasInitializedRef = useRef(false);
    const saveProgressRef = useRef(null); // Always points to latest saveProgress for use in cleanup/beforeunload

    const fetchTestData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null); // Clear any previous errors

            if (!user) {
                throw new Error('User not authenticated');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/viewTests/${subscriptionId}?test=${encodeURIComponent(testId)}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.data && response.data.status === 'success') {
                setTestData(response.data.data.category);
                const fetchedQuestions = response.data.data.category.question_ids;

                if (Array.isArray(fetchedQuestions) && fetchedQuestions.length > 0) {
                    // Create a map of original indices to questions
                    const qMap = new Map();
                    fetchedQuestions.forEach((q, index) => {
                        qMap.set(q._id, index);
                    });
                    setQuestionMap(qMap);

                    setQuestions(fetchedQuestions);
                    setOriginalQuestions(fetchedQuestions);

                    // Store the randomized order
                    const order = fetchedQuestions.map((q) => q._id);
                    setQuestionOrder(order);
                } else {
                    throw new Error('No questions found for this test');
                }
            } else {
                throw new Error('Invalid test data received');
            }
        } catch (err) {
            console.error('Error fetching test data:', err);

            // Provide user-friendly error messages
            let errorMessage = 'Failed to load test data';

            if (err.message === 'Test not found' || err.response?.data?.error === 'Test not found') {
                errorMessage = 'This test could not be found. Please check your subscription or contact support.';
            } else if (err.response?.status === 401 || err.response?.status === 403) {
                errorMessage = 'Your session has expired. Please log in again.';
                setTimeout(() => navigate('/subscriptions'), 2000);
            } else if (err.message === 'No questions found for this test') {
                errorMessage = 'This test has no questions available. Please contact support.';
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [subscriptionId, testId, user, navigate]);

    const fetchFullQuestionData = useCallback(async (questionId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (response.data) {
                // Ensure the correct_answer is a letter (A, B, C, or D)
                const question = response.data;
                if (!['A', 'B', 'C', 'D'].includes(question.correct_answer)) {
                    console.error('Invalid correct_answer format:', question.correct_answer);
                    question.correct_answer = 'A'; // Default to 'A' if invalid
                }
                return question;
            } else {
                throw new Error('Invalid question data received');
            }
        } catch (err) {
            console.error('Error fetching full question data:', err);
            setError('Failed to fetch question details. Please try again.');
            return null;
        }
    }, []);

    useEffect(() => {
        // Add a class to the body when the component mounts
        document.body.classList.add('test-page-active');

        // Find and hide the original headers
        const topHeader = document.querySelector('.MuiBox-root'); // Target the TopHeader's Box component
        const mainHeader = document.querySelector('header'); // Target your main Header

        if (topHeader) topHeader.style.display = 'none';
        if (mainHeader) mainHeader.style.display = 'none';

        // Create a style element for the test page
        const styleElement = document.createElement('style');
        styleElement.textContent = `
        .test-page-active .MuiBox-root:not(.screenshot-protected *),
        .test-page-active header:not(.MuiAppBar-root) {
            display: none !important;
        }
    `;
        document.head.appendChild(styleElement);

        return () => {
            // Clean up when component unmounts
            document.body.classList.remove('test-page-active');
            if (topHeader) topHeader.style.display = '';
            if (mainHeader) mainHeader.style.display = '';
            document.head.removeChild(styleElement);
        };
    }, []);

    useEffect(() => {
        let timeoutId;

        const handleMouseMove = (e) => {
            // Increased detection area to be more user-friendly
            if (e.clientY < 150) {
                // Increased from 100 to 150
                setShowHeader(true);
                clearTimeout(timeoutId);
            } else {
                timeoutId = setTimeout(() => {
                    setShowHeader(false);
                }, 1500); // Increased from 1000 to 1500ms for better usability
            }
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timeoutId);
        };
    }, []);

    // Combined effect to fetch test data and restore progress
    // IMPORTANT: This must only run ONCE on mount (or when subscriptionId/testId change).
    // The `user` dependency can get a new reference when auth context refreshes subscriptions,
    // which would re-run this effect and overwrite retake mode questions with the full test.
    useEffect(() => {
        const loadTestDataAndProgress = async () => {
            try {
                // Prevent re-initialization when user reference changes (e.g., subscription refresh)
                if (hasInitializedRef.current) {
                    return;
                }

                setLoading(true);
                setError(null);

                if (!user) {
                    setError('User not authenticated');
                    setLoading(false);
                    return;
                }

                // Mark as initialized so we don't re-run on user reference changes
                hasInitializedRef.current = true;

                // Fetch user progress from server FIRST - this is the source of truth
                const progressData = await userProgressService.getUserProgress(subscriptionId, testId);

                // Load saved progress from localStorage as backup
                const savedData = localStorage.getItem(`testProgress_${subscriptionId}_${testId}`);
                let savedOrder = null;

                // Store missed question IDs for later reconstruction (after we have questionMap)
                let savedMissedQuestionIds = [];

                if (savedData) {
                    const parsedData = JSON.parse(savedData);
                    setResumeData(parsedData);

                    // Store missed questions (now stored as IDs, but handle legacy full objects too)
                    if (parsedData.missedQuestions?.length > 0) {
                        savedMissedQuestionIds = parsedData.missedQuestions.map((q) =>
                            typeof q === 'string' ? q : q._id
                        ).filter(Boolean);
                    }

                    savedOrder = parsedData.questionOrder;
                }

                // Prioritize server's question order over localStorage
                const serverOrder = progressData?.questionOrder;
                const questionOrderToUse = serverOrder && serverOrder.length > 0 ? serverOrder : savedOrder;

                // Fetch test data inline (avoid dependency on fetchTestData callback)
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/viewTests/${subscriptionId}?test=${encodeURIComponent(testId)}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                });

                // Declare orderedQuestions at higher scope so it's accessible in progressData block
                let orderedQuestions;

                if (response.data && response.data.status === 'success') {
                    setTestData(response.data.data.category);
                    const fetchedQuestions = response.data.data.category.question_ids;

                    if (Array.isArray(fetchedQuestions) && fetchedQuestions.length > 0) {
                        // Create a map for quick lookup of questions by ID
                        const qMap = new Map();
                        fetchedQuestions.forEach((q) => {
                            qMap.set(q._id, q);
                        });
                        setQuestionMap(qMap);

                        let finalQuestionOrder;

                        // If we have a saved question order, use it to reorder the fetched questions
                        if (questionOrderToUse && questionOrderToUse.length > 0) {
                            console.log('Restoring question order from saved state');
                            orderedQuestions = questionOrderToUse
                                .map((qId) => qMap.get(qId))
                                .filter((q) => q !== undefined); // Filter out any questions that no longer exist

                            // If some questions are missing from the order (new questions added?), append them
                            const orderedIds = new Set(questionOrderToUse);
                            const newQuestions = fetchedQuestions.filter((q) => !orderedIds.has(q._id));
                            if (newQuestions.length > 0) {
                                console.log('Found new questions not in saved order, appending them');
                                orderedQuestions = [...orderedQuestions, ...newQuestions];
                            }

                            finalQuestionOrder = orderedQuestions.map((q) => q._id);
                        } else {
                            // First time taking this test - use fetched order and save it
                            console.log('No saved question order found, using fetched order');
                            orderedQuestions = fetchedQuestions;
                            finalQuestionOrder = fetchedQuestions.map((q) => q._id);
                        }

                        setOriginalQuestions(orderedQuestions);
                        setQuestionOrder(finalQuestionOrder);

                        // Get missed question IDs from server (preferred) or localStorage
                        const serverMissedIds = progressData?.missedQuestions?.map((q) =>
                            typeof q === 'string' ? q : q._id
                        ).filter(Boolean) || [];
                        const missedIdsToUse = serverMissedIds.length > 0 ? serverMissedIds : savedMissedQuestionIds;

                        // Handle retake mode - reconstruct missed questions from IDs
                        if (action === 'retake' && missedIdsToUse.length > 0) {
                            console.log('Retake mode: reconstructing missed questions from IDs');
                            const missedQuestionObjects = missedIdsToUse
                                .map((qId) => qMap.get(qId))
                                .filter(Boolean);

                            if (missedQuestionObjects.length > 0) {
                                setQuestions(missedQuestionObjects);
                                setIsRetakingMissed(true);
                                // Store the IDs for tracking
                                setMissedQuestions(missedIdsToUse);
                            } else {
                                console.warn('Could not reconstruct missed questions, using full test');
                                setQuestions(orderedQuestions);
                            }
                        } else {
                            setQuestions(orderedQuestions);
                            // Store missed question IDs for tracking (not retaking yet)
                            if (missedIdsToUse.length > 0) {
                                setMissedQuestions(missedIdsToUse);
                            }
                        }
                    } else {
                        throw new Error('No questions found for this test');
                    }
                } else {
                    throw new Error('Invalid test data received');
                }

                if (progressData) {
                    setUserProgress(progressData);

                    // Restore full state from server if available (skip for retake mode)
                    if (action !== 'retake') {
                        // Compare server progress with localStorage and use the MORE ADVANCED one.
                        // This handles the case where server saves failed silently (e.g., token expired,
                        // network issue, or beforeunload async save didn't complete) but localStorage
                        // has the correct progress.
                        const serverIndex = progressData.currentQuestionIndex ?? 0;
                        const serverScore = progressData.correctAnswers ?? 0;
                        const serverIncorrect = progressData.inCorrectAnswers ?? 0;
                        const serverAttempted = progressData.attemptedQuestions ?? [];
                        const serverFirstCorrect = progressData.firstAttemptCorrect ?? [];

                        let localIndex = 0;
                        let localScore = 0;
                        let localIncorrect = 0;
                        let localAttempted = [];
                        let localFirstCorrect = [];
                        let useLocalStorage = false;

                        if (savedData) {
                            const parsedLocal = JSON.parse(savedData);
                            localIndex = parsedLocal.currentQuestionIndex ?? 0;
                            localScore = parsedLocal.score ?? 0;
                            localIncorrect = parsedLocal.incorrectAnswers ?? 0;
                            localAttempted = parsedLocal.attemptedQuestions ?? [];
                            localFirstCorrect = parsedLocal.firstAttemptCorrect ?? [];

                            // Use localStorage if it has more progress than server
                            // (more attempted questions = more progress made)
                            const serverAttemptedCount = serverAttempted.length;
                            const localAttemptedCount = localAttempted.length;

                            if (localAttemptedCount > serverAttemptedCount) {
                                console.warn(
                                    `Server progress behind localStorage! Server attempted: ${serverAttemptedCount}, ` +
                                    `localStorage attempted: ${localAttemptedCount}. Using localStorage data.`
                                );
                                useLocalStorage = true;
                            } else if (localAttemptedCount === serverAttemptedCount && localIndex > serverIndex) {
                                console.warn(
                                    `Server question index behind localStorage! Server index: ${serverIndex}, ` +
                                    `localStorage index: ${localIndex}. Using localStorage data.`
                                );
                                useLocalStorage = true;
                            }
                        }

                        if (useLocalStorage && orderedQuestions) {
                            const validatedIndex = Math.min(
                                Math.max(0, localIndex),
                                orderedQuestions.length - 1
                            );
                            setCurrentQuestionIndex(validatedIndex);
                            setScore(localScore);
                            setIncorrectAnswers(localIncorrect);
                            setAttemptedQuestions(new Set(localAttempted));
                            setFirstAttemptCorrect(new Set(localFirstCorrect));
                            console.log(`Restored from localStorage: question ${validatedIndex + 1}, score ${localScore}`);
                        } else if (orderedQuestions) {
                            const validatedIndex = Math.min(
                                Math.max(0, serverIndex),
                                orderedQuestions.length - 1
                            );
                            setCurrentQuestionIndex(validatedIndex);

                            if (validatedIndex !== serverIndex) {
                                console.warn(
                                    `Question index ${serverIndex} out of bounds, ` +
                                    `adjusted to ${validatedIndex} (total questions: ${orderedQuestions.length})`
                                );
                            }
                            setScore(serverScore);
                            setIncorrectAnswers(serverIncorrect);
                            setAttemptedQuestions(new Set(serverAttempted));
                            setFirstAttemptCorrect(new Set(serverFirstCorrect));
                            console.log(`Restored from server: question ${validatedIndex + 1}, score ${serverScore}`);
                        }
                    } else {
                        // Retake mode - reset scores to 0
                        setCurrentQuestionIndex(0);
                        setScore(0);
                        setIncorrectAnswers(0);
                        setAttemptedQuestions(new Set());
                        setFirstAttemptCorrect(new Set());
                    }
                }
            } catch (err) {
                console.error('Error loading test data and progress:', err);

                // Provide user-friendly error messages
                let errorMessage = 'Failed to load test data';

                if (err.message === 'Test not found' || err.response?.data?.error === 'Test not found') {
                    errorMessage = 'This test could not be found. Please check your subscription or contact support.';
                } else if (err.response?.status === 401 || err.response?.status === 403) {
                    errorMessage = 'Your session has expired. Please log in again.';
                    setTimeout(() => navigate('/subscriptions'), 2000);
                } else if (err.message === 'No questions found for this test') {
                    errorMessage = 'This test has no questions available. Please contact support.';
                } else if (err.response?.data?.message) {
                    errorMessage = err.response.data.message;
                }

                setError(errorMessage);
                // Allow retry on failure
                hasInitializedRef.current = false;
            } finally {
                setLoading(false);
            }
        };

        loadTestDataAndProgress();
    }, [subscriptionId, testId, user, action, navigate]);

    // Fallback: if server had NO real progress but localStorage does, use localStorage.
    // NOTE: The primary server-vs-localStorage comparison now happens in loadTestDataAndProgress above.
    // This effect is a safety net for edge cases where userProgress exists but has no meaningful data
    // (e.g., newly auto-created record) and localStorage has actual progress.
    useEffect(() => {
        // Check if server progress has any REAL data (not just defaults)
        const hasRealServerProgress = userProgress && (
            (userProgress.attemptedQuestions?.length > 0) ||
            (userProgress.currentQuestionIndex > 0) ||
            (userProgress.correctAnswers > 0) ||
            (userProgress.inCorrectAnswers > 0)
        );

        if (!hasRealServerProgress && resumeData && action !== 'retake') {
            // Server has only defaults but localStorage has progress — use localStorage
            const localAttempted = resumeData.attemptedQuestions ?? [];
            if (localAttempted.length > 0 || (resumeData.currentQuestionIndex ?? 0) > 0) {
                console.log('Fallback: using localStorage data (server has no real progress)');
                setCurrentQuestionIndex(resumeData.currentQuestionIndex ?? 0);
                setScore(resumeData.score ?? 0);
                setIncorrectAnswers(resumeData.incorrectAnswers ?? 0);
                setAttemptedQuestions(new Set(localAttempted));
                setFirstAttemptCorrect(new Set(resumeData.firstAttemptCorrect ?? []));
            }
        } else if (resumeData && action === 'retake') {
            setCurrentQuestionIndex(0);
            setScore(0);
            setIncorrectAnswers(0);
            setAttemptedQuestions(new Set());
            setFirstAttemptCorrect(new Set());
        }
    }, [resumeData, action, userProgress]);

    // Fetch full question data when needed
    useEffect(() => {
        if (questions.length > 0 && currentQuestionIndex < questions.length) {
            const question = questions[currentQuestionIndex];
            if (question && (!question.question_text || !question.answers || !question.correct_answer)) {
                fetchFullQuestionData(question._id).then((fullQuestion) => {
                    if (fullQuestion) {
                        setCurrentQuestion(fullQuestion);
                        setQuestions((prevQuestions) => {
                            const updatedQuestions = [...prevQuestions];
                            updatedQuestions[currentQuestionIndex] = fullQuestion;
                            return updatedQuestions;
                        });
                    }
                });
            } else {
                setCurrentQuestion(question);
            }
        }
    }, [questions, currentQuestionIndex, fetchFullQuestionData]);

    const generateWatermark = useCallback(() => {
        if (user?.email && user?.firstName && user?.lastName) {
            // Create full name by combining firstName and lastName
            const fullName = `${user.firstName} ${user.lastName}`;
            const watermarkText = `${fullName} - ${user.email}`;
            setWatermarkText(watermarkText);

            // Apply watermark through styled element with improved styling
            const watermarkStyle = document.createElement('style');
            watermarkStyle.textContent = `
            .screenshot-protected::after {
                content: "${watermarkText}";
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 28px;
                opacity: 0.1;
                pointer-events: none;
                z-index: 9999;
                white-space: nowrap;
                user-select: none;
                font-family: "Poppins", sans-serif;
                width: 100%;
                text-align: center;
                color: #440;
                letter-spacing: 0.5px;
                text-transform: capitalize;  // Ensures names are properly capitalized
                background: repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 100px,
                    rgba(102, 102, 102, 0.05) 100px,
                    rgba(102, 102, 102, 0.05) 200px
                );  // Adds subtle pattern to the watermark
                padding: 20px;
            }
            
            /* Add multiple watermarks for better coverage */
            .screenshot-protected::before {
                content: "${watermarkText}";
                position: fixed;
                top: 25%;
                left: 25%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 20px;
                opacity: 0.05;
                pointer-events: none;
                z-index: 9999;
                white-space: nowrap;
                user-select: none;
                font-family: "Poppins", sans-serif;
            }
        `;
            document.head.appendChild(watermarkStyle);

            return () => {
                document.head.removeChild(watermarkStyle);
            };
        }
    }, [user?.email, user?.firstName, user?.lastName]); // Updated dependencies

    // Add security logging function
    const logSecurityEvent = useCallback(
        async (eventType, details = {}) => {
            try {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/security-logs/create`, {
                    userId: user?.id,
                    testId,
                    subscriptionId,
                    eventType,
                    details,
                    timestamp: new Date().toISOString(),
                    questionId: currentQuestion?._id,
                    currentQuestionIndex,
                });
            } catch (error) {
                console.error('Failed to log security event:', error);
            }
        },
        [user?.id, testId, subscriptionId, currentQuestion?._id, currentQuestionIndex],
    );

    // Add this useEffect
    useEffect(() => {
        // Reset answer states when question changes
        setSelectedAnswer(null);
        setCorrectAnswer(null);
        setShowConfetti(false);
    }, [currentQuestionIndex]);

    // Time tracking effect
    useEffect(() => {
        // Function to sync time spent with backend
        const syncTimeSpent = async () => {
            const now = Date.now();
            const timeSinceLastSync = Math.floor((now - timeTrackingRef.current.lastSyncTime) / 1000); // in seconds

            if (timeSinceLastSync > 0) {
                try {
                    await axios.post(
                        `${process.env.REACT_APP_API_URL}/api/users/track-time`,
                        { timeSpent: timeSinceLastSync },
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                        }
                    );
                    timeTrackingRef.current.lastSyncTime = now;
                    timeTrackingRef.current.accumulatedTime += timeSinceLastSync;
                } catch (error) {
                    console.error('Error tracking time:', error);
                    // Don't block user experience if time tracking fails
                }
            }
        };

        // Sync time every 60 seconds
        timeTrackingIntervalRef.current = setInterval(() => {
            syncTimeSpent();
        }, 60000); // 60 seconds

        // Cleanup function
        return () => {
            if (timeTrackingIntervalRef.current) {
                clearInterval(timeTrackingIntervalRef.current);
            }
            // Final sync on unmount
            syncTimeSpent();
        };
    }, []); // Empty dependency array - only run on mount/unmount

    // Modify the existing security effect to include logging
    useEffect(() => {
        // Set up watermark
        const cleanupWatermark = generateWatermark();

        // Disable right-click
        const handleContextMenu = (e) => {
            e.preventDefault();
            logSecurityEvent('RIGHT_CLICK_ATTEMPT');
            return false;
        };

        // Disable keyboard shortcuts and print screen
        const handleKeyDown = (e) => {
            let eventType = null;

            // Prevent PrintScreen
            if (e.key === 'PrintScreen') {
                eventType = 'PRINT_SCREEN_ATTEMPT';
                e.preventDefault();
            }
            // Prevent Ctrl+P (Print)
            else if (e.ctrlKey && e.key === 'p') {
                eventType = 'PRINT_ATTEMPT';
                e.preventDefault();
            }
            // Prevent Ctrl+Shift+I (Developer Tools)
            else if (e.ctrlKey && e.shiftKey && e.key === 'I') {
                eventType = 'DEV_TOOLS_ATTEMPT';
                e.preventDefault();
            }
            // Prevent Ctrl+Shift+C (Developer Tools)
            else if (e.ctrlKey && e.shiftKey && e.key === 'C') {
                eventType = 'DEV_TOOLS_ATTEMPT';
                e.preventDefault();
            }
            // Prevent Ctrl+C (Copy)
            else if (e.ctrlKey && e.key === 'c') {
                eventType = 'COPY_ATTEMPT';
                e.preventDefault();
            }
            // Prevent Ctrl+S (Save)
            else if (e.ctrlKey && e.key === 's') {
                eventType = 'SAVE_ATTEMPT';
                e.preventDefault();
            }

            if (eventType) {
                logSecurityEvent(eventType, {
                    key: e.key,
                    ctrlKey: e.ctrlKey,
                    shiftKey: e.shiftKey,
                });
            }
            return false;
        };

        // Disable copy/paste
        const handleCopy = (e) => {
            e.preventDefault();
            logSecurityEvent('COPY_ATTEMPT');
            return false;
        };

        // Disable selection
        const handleSelect = (e) => {
            e.preventDefault();
            logSecurityEvent('SELECTION_ATTEMPT');
            return false;
        };

        // Handle visibility change (tab switching)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                logSecurityEvent('TAB_SWITCH', {
                    visible: !document.hidden,
                    timestamp: new Date().toISOString(),
                });
            }
        };

        // Add event listeners
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('copy', handleCopy);
        document.addEventListener('selectstart', handleSelect);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup function
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('selectstart', handleSelect);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (cleanupWatermark) cleanupWatermark();
        };
    }, [generateWatermark, logSecurityEvent]);

    // Add logging to component mounting
    useEffect(() => {
        logSecurityEvent('TEST_PAGE_LOADED');

        return () => {
            logSecurityEvent('TEST_PAGE_UNLOADED');
        };
    }, [logSecurityEvent]);

    const initializeTestState = () => {
        setCurrentQuestionIndex(0);
        setScore(0);
        setIncorrectAnswers(0);
        setAttemptedQuestions(new Set());
        setFirstAttemptCorrect(new Set());
    };

    // Helper function to restore progress
    const restoreProgress = (parsedData) => {
        setCurrentQuestionIndex(parsedData.currentQuestionIndex || 0);
        setScore(parsedData.score || 0);
        setIncorrectAnswers(parsedData.incorrectAnswers || 0);
        setAttemptedQuestions(new Set(parsedData.attemptedQuestions || []));
        setFirstAttemptCorrect(new Set(parsedData.firstAttemptCorrect || []));
        setMissedQuestions(parsedData.missedQuestions || []);
    };

    // Convert missedQuestions to IDs for storage (not full objects)
    const getMissedQuestionIds = useCallback(() => {
        return missedQuestions.map(q => typeof q === 'string' ? q : q._id).filter(Boolean);
    }, [missedQuestions]);

    const saveProgress = useCallback(async () => {
        try {
            // Only store IDs for missed questions, not full objects (saves ~95% storage)
            const missedQuestionIds = getMissedQuestionIds();

            const progressData = {
                currentQuestionIndex,
                score,
                incorrectAnswers,
                attemptedQuestions: Array.from(attemptedQuestions),
                firstAttemptCorrect: Array.from(firstAttemptCorrect),
                missedQuestions: missedQuestionIds, // Store only IDs
                questionOrder: questionOrder,
                isRetakingMissed, // Track mode for proper restoration
            };

            // Try to save to localStorage with quota handling
            try {
                localStorage.setItem(`testProgress_${subscriptionId}_${testId}`, JSON.stringify(progressData));
            } catch (storageError) {
                // Handle localStorage quota exceeded
                if (storageError.name === 'QuotaExceededError' || storageError.code === 22) {
                    console.warn('localStorage quota exceeded, clearing old data');
                    // Clear this test's data and try again with minimal data
                    localStorage.removeItem(`testProgress_${subscriptionId}_${testId}`);
                    const minimalData = {
                        currentQuestionIndex,
                        score,
                        incorrectAnswers,
                        questionOrder,
                    };
                    localStorage.setItem(`testProgress_${subscriptionId}_${testId}`, JSON.stringify(minimalData));
                }
            }

            // Save to server (source of truth)
            if (userProgress?._id) {
                const currentQuestionId = questions[currentQuestionIndex]?._id || null;

                await userProgressService.updateUserProgressWithRetry(userProgress._id, {
                    correctAnswers: score,
                    inCorrectAnswers: incorrectAnswers,
                    currentQuestionIndex,
                    questionOrder,
                    lastQuestionId: currentQuestionId,
                    attemptedQuestions: Array.from(attemptedQuestions),
                    firstAttemptCorrect: Array.from(firstAttemptCorrect),
                    missedQuestions: missedQuestionIds, // Store only IDs on server too
                });
            }
        } catch (error) {
            console.error('Error saving progress:', error);
            // Don't show error to user for save failures - server is source of truth
        }
    }, [
        currentQuestionIndex,
        score,
        incorrectAnswers,
        attemptedQuestions,
        firstAttemptCorrect,
        getMissedQuestionIds,
        isRetakingMissed,
        subscriptionId,
        testId,
        userProgress,
        questionOrder,
        questions,
    ]);

    // Keep ref in sync so cleanup/beforeunload always call the latest version
    useEffect(() => {
        saveProgressRef.current = saveProgress;
    }, [saveProgress]);

    // Debounced auto-save - prevents excessive saves on rapid state changes
    const saveTimeoutRef = useRef(null);
    useEffect(() => {
        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        // Debounce save by 500ms
        saveTimeoutRef.current = setTimeout(() => {
            saveProgress();
        }, 500);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [saveProgress]);

    // Save progress on page close/navigation and component unmount
    // Uses navigator.sendBeacon() for beforeunload to guarantee the request
    // is delivered even when the page is being killed by the browser.
    // Regular async fetch/axios calls are NOT reliable in beforeunload handlers.
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Flush any pending debounced save immediately
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Synchronous localStorage save (always completes)
            if (saveProgressRef.current) {
                saveProgressRef.current();
            }

            // Use sendBeacon for reliable server save on page unload
            // sendBeacon is guaranteed to be queued even as the page is closing
            try {
                const progressId = userProgress?._id;
                if (progressId && navigator.sendBeacon) {
                    const missedQuestionIds = missedQuestions.map(q =>
                        typeof q === 'string' ? q : q._id
                    ).filter(Boolean);
                    const currentQuestionId = questions[currentQuestionIndex]?._id || null;

                    const beaconData = JSON.stringify({
                        correctAnswers: score,
                        inCorrectAnswers: incorrectAnswers,
                        currentQuestionIndex,
                        questionOrder,
                        lastQuestionId: currentQuestionId,
                        attemptedQuestions: Array.from(attemptedQuestions),
                        firstAttemptCorrect: Array.from(firstAttemptCorrect),
                        missedQuestions: missedQuestionIds,
                    });

                    const token = localStorage.getItem('token');
                    const blob = new Blob([beaconData], { type: 'application/json' });
                    // sendBeacon doesn't support custom headers, so pass token in query string
                    const url = `${process.env.REACT_APP_API_URL}/api/userProgress/${progressId}?beacon=true&token=${encodeURIComponent(token)}`;
                    navigator.sendBeacon(url, blob);
                }
            } catch (e) {
                // Best-effort — localStorage save above already completed
                console.error('sendBeacon failed:', e);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Component unmount (e.g. navigating away or logging out):
            // flush pending debounced save and do one final save
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (saveProgressRef.current) {
                saveProgressRef.current();
            }
        };
    }, [userProgress, questions, currentQuestionIndex, score, incorrectAnswers,
        questionOrder, attemptedQuestions, firstAttemptCorrect, missedQuestions]);
    
    // Handle window resizing with debounce to prevent ResizeObserver loops
    useEffect(() => {
        let timeoutId = null;
        
        const handleResize = () => {
            // Clear previous timeout to debounce the resize event
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            
            // Set a new timeout to update dimensions after a delay
            timeoutId = setTimeout(() => {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }, 200); // 200ms debounce
        };
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Initial size set
        handleResize();
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const handleAnswerSelection = async (index) => {
        if (!currentQuestion) return;

        // Log the answer selection
        await logSecurityEvent('ANSWER_SELECTED', {
            questionIndex: currentQuestionIndex,
            selectedAnswerIndex: index,
        });

        const selectedLetter = ['A', 'B', 'C', 'D'][index];
        const isCorrect = selectedLetter === currentQuestion.correct_answer;

        setSelectedAnswer(index);
        setCorrectAnswer(isCorrect);

        if (!attemptedQuestions.has(currentQuestion._id)) {
            setAttemptedQuestions((prev) => new Set(prev).add(currentQuestion._id));

            if (isCorrect) {
                setScore((prevScore) => prevScore + 1);
                setFirstAttemptCorrect((prev) => new Set(prev).add(currentQuestion._id));
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 3000);
            } else {
                setIncorrectAnswers((prev) => prev + 1);
                if (!isRetakingMissed) {
                    // Store only the question ID, not the full object (saves storage)
                    setMissedQuestions((prevMissed) => {
                        const qId = currentQuestion._id;
                        // Check if ID already exists (handle both ID strings and legacy objects)
                        const alreadyExists = prevMissed.some((q) =>
                            (typeof q === 'string' ? q : q._id) === qId
                        );
                        if (!alreadyExists) {
                            return [...prevMissed, qId];
                        }
                        return prevMissed;
                    });
                }
            }

            // Update cumulative statistics
            try {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/users/update-question-stats`,
                    { isCorrect },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );
            } catch (error) {
                console.error('Error updating cumulative stats:', error);
                // Continue even if stats update fails
            }

            // Save progress after updating score
            await saveProgress();
        }
    };

    const handleNextQuestion = async () => {
        // Prevent moving to next question if no answer is selected
        if (selectedAnswer === null) {
            return;
        }

        // Clear all answer-related states
        setSelectedAnswer(null);
        setCorrectAnswer(null);
        setShowConfetti(false);

        // Change question index
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        } else {
            // Test completed - track the score
            setTestCompleted(true);
            setOpenDialog(true);

            // Track test score
            try {
                const testScore = {
                    subscriptionId,
                    testId,
                    testName: testData?.name || 'Unknown Test',
                    correctAnswers: score,
                    incorrectAnswers,
                    totalQuestions: questions.length,
                    missedQuestions: missedQuestions.map(q => {
                        // Handle both ID strings and legacy full objects
                        const qId = typeof q === 'string' ? q : q._id;
                        const fullQ = questionMap.get(qId);
                        return {
                            questionId: qId,
                            questionText: fullQ?.question_text || q?.question_text || 'Unknown',
                        };
                    }),
                    testType: isRetakingMissed ? 'retake' : 'practice',
                };

                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/user-history/track-test-score`,
                    testScore,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );
                console.log('Test score tracked successfully');
            } catch (error) {
                console.error('Error tracking test score:', error);
                // Continue even if tracking fails
            }

            if (!isRetakingMissed) {
                localStorage.removeItem(`testProgress_${subscriptionId}_${testId}`);
            }
        }

        saveProgress();
    };

    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const handleResetTest = async () => {
        try {
            // Shuffle the original questions
            const shuffledQuestions = shuffleArray(originalQuestions);
            setQuestions(shuffledQuestions);

            // Update question order
            const newOrder = shuffledQuestions.map((q) => q._id);
            setQuestionOrder(newOrder);

            initializeTestState();
            setSelectedAnswer(null);
            setCorrectAnswer(null);
            setMissedQuestions([]);
            setTestCompleted(false);

            localStorage.removeItem(`testProgress_${subscriptionId}_${testId}`);

            // Reset server progress with new question order
            if (userProgress?._id) {
                await userProgressService.updateUserProgress(userProgress._id, {
                    correctAnswers: 0,
                    inCorrectAnswers: 0,
                    currentQuestionIndex: 0,
                    questionOrder: newOrder,
                    lastQuestionId: null,
                    attemptedQuestions: [],
                    firstAttemptCorrect: [],
                    missedQuestions: [],
                });
            }
        } catch (error) {
            console.error('Error resetting test:', error);
            setError('Failed to reset test progress. Please try again.');
        }
    };

    const handleRetakeMissedQuestions = () => {
        if (missedQuestions.length > 0) {
            // Reconstruct full question objects from IDs using questionMap or originalQuestions
            const missedQuestionObjects = missedQuestions
                .map((q) => {
                    const qId = typeof q === 'string' ? q : q._id;
                    // Try questionMap first, then search originalQuestions
                    return questionMap.get(qId) || originalQuestions.find((oq) => oq._id === qId);
                })
                .filter(Boolean); // Remove any that couldn't be found

            if (missedQuestionObjects.length === 0) {
                console.error('Could not reconstruct missed questions from IDs');
                return;
            }

            setQuestions(missedQuestionObjects);
            setCurrentQuestionIndex(0);
            setSelectedAnswer(null);
            setCorrectAnswer(null);
            setIsRetakingMissed(true);
            setScore(0);
            setIncorrectAnswers(0);
            setAttemptedQuestions(new Set());
            setFirstAttemptCorrect(new Set());
            setOpenDialog(false);
        }
    };

    const handleResumeFullTest = () => {
        // DO NOT reshuffle - restore the original question order
        setQuestions(originalQuestions);
        setIsRetakingMissed(false);
        setCurrentQuestionIndex(0);
        setSelectedAnswer(null);
        setCorrectAnswer(null);
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            // Reset states before changing question
            setSelectedAnswer(null);
            setCorrectAnswer(null);
            setShowConfetti(false);
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleRetry = () => {
        setError(null);
        setLoading(true);
        window.location.reload();
    };

    // Better error display
    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 4,
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        boxShadow: 3,
                    }}
                >
                    <ErrorIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        Unable to Load Test
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                        {error}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleRetry}
                            sx={{
                                backgroundColor: '#ff5e1c',
                                '&:hover': { backgroundColor: '#e65419' },
                            }}
                        >
                            Try Again
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/subscriptions')}>
                            Back to Subscriptions
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (!testData || questions.length === 0) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        No test data available
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        This test doesn't have any questions yet. Please contact support or try another test.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => navigate('/subscriptions')}>
                        View Your Subscriptions
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (loading || !currentQuestion) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress size={60} sx={{ color: '#ff5e1c', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                    Loading your test...
                </Typography>
            </Box>
        );
    }

    const answers = [
        currentQuestion.answers.answer_a,
        currentQuestion.answers.answer_b,
        currentQuestion.answers.answer_c,
        currentQuestion.answers.answer_d,
    ];

    //handle Ask AI Assitant
    const handleAIAssitant = () => {
        setIsWebchatOpen((prevState) => !prevState);
    };

    const renderReferenceAndSolution = () => {
        if (!currentQuestion || selectedAnswer === null) return null;

        return (
            <Fade in timeout={500}>
                <Box sx={{ mt: 4 }}>
                    {(currentQuestion.reference || currentQuestion.solution) && (
                        <Grid
                            container
                            spacing={3}
                            justifyContent="center"
                            alignItems="stretch"
                            sx={{
                                maxWidth: '1000px',
                                margin: '0 auto',
                                px: { xs: 2, md: 4 }, // Add padding on the sides
                            }}
                        >
                            {currentQuestion.reference && (
                                <Grid item xs={12} md={currentQuestion.solution ? 6 : 8}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            backgroundColor: 'rgba(255, 94, 28, 0.02)',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: (theme) => theme.shadows[10],
                                            },
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <CardHeader
                                            avatar={
                                                <MenuBookIcon
                                                    sx={{
                                                        color: '#ff5e1c',
                                                        fontSize: 28,
                                                    }}
                                                />
                                            }
                                            title={
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: '#ff5e1c',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Reference
                                                </Typography>
                                            }
                                            sx={{ pb: 0 }}
                                        />
                                        <CardContent
                                            sx={{
                                                pt: 2,
                                                flex: 1, // Make card content fill available space
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: 'text.primary',
                                                    lineHeight: 1.7,
                                                    fontFamily: (theme) => theme.typography.fontFamily,
                                                    flex: 1, // Make text fill available space
                                                }}
                                            >
                                                {currentQuestion.reference}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {currentQuestion.solution && (
                                <Grid item xs={12} md={currentQuestion.reference ? 6 : 8}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            backgroundColor: correctAnswer ? 'rgba(76, 175, 80, 0.02)' : 'rgba(244, 67, 54, 0.02)',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: (theme) => theme.shadows[10],
                                            },
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <CardHeader
                                            avatar={
                                                correctAnswer ? (
                                                    <CheckCircleIcon
                                                        sx={{
                                                            color: 'success.main',
                                                            fontSize: 28,
                                                        }}
                                                    />
                                                ) : (
                                                    <ErrorIcon
                                                        sx={{
                                                            color: 'error.main',
                                                            fontSize: 28,
                                                        }}
                                                    />
                                                )
                                            }
                                            title={
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: correctAnswer ? 'success.main' : 'error.main',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Solution Explanation
                                                </Typography>
                                            }
                                            sx={{ pb: 0 }}
                                        />
                                        <CardContent
                                            sx={{
                                                pt: 2,
                                                flex: 1, // Make card content fill available space
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: 'text.primary',
                                                    lineHeight: 1.7,
                                                    fontFamily: (theme) => theme.typography.fontFamily,
                                                    flex: 1, // Make text fill available space
                                                }}
                                            >
                                                {currentQuestion.solution}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </Fade>
        );
    };

    return (
        <Container
            maxWidth="md"
            sx={{
                mt: { xs: 2, sm: 3, md: 5 }, // Responsive margin top
                position: 'relative',
                transition: 'margin-top 0.3s ease', // Smooth transition when header slides
            }}
            className="screenshot-protected"
        >
            <EnhancedHeader TopHeader={TopHeader} Header={Header} />
            {showConfetti && <Confetti 
                width={windowSize.width} 
                height={windowSize.height} 
                recycle={false}
                numberOfPieces={200}
                tweenDuration={5000}
            />}

            <Fade in timeout={500}>
                <Box sx={{ position: 'relative', zIndex: 1, p: 4 }}>
                    <LinearProgress
                        variant="determinate"
                        // value={
                        //     ((currentQuestionIndex + 1) / questions.length) *
                        //     100
                        // }
                        value={((score + incorrectAnswers) / questions.length) * 100}
                        sx={{
                            mb: 3,
                            height: 12,
                            backgroundColor: '#ddd',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#ff5e1c',
                            },
                            borderRadius: 2,
                        }}
                    />

                    <Typography
                        variant="h3"
                        color="primary"
                        gutterBottom
                        align="center"
                        sx={{
                            fontWeight: 'bold',
                            letterSpacing: '0.05em',
                            background: 'linear-gradient(45deg, #ff5e1c 30%, #ff9200 90%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontFamily: '"Poppins", sans-serif',
                        }}
                    >
                        {testData.name}
                    </Typography>

                    <Typography variant="subtitle1" align="center" gutterBottom sx={{ mb: 1 }}>
                        <span style={{ fontWeight: 600, color: '#ff5e1c' }}>Question {currentQuestionIndex + 1}</span> of {questions.length}
                    </Typography>

                    <Typography variant="subtitle1" align="center" gutterBottom sx={{ mb: 3 }}>
                        <span style={{ fontWeight: 600, color: '#4caf50' }}>{score} Correct</span> |{' '}
                        <span style={{ fontWeight: 600, color: '#f44336' }}>{incorrectAnswers} Incorrect</span>
                    </Typography>

                    <Typography
                        variant="h5"
                        gutterBottom
                        align="center"
                        sx={{
                            mb: 4,
                            fontFamily: '"Poppins", sans-serif',
                            color: '#333',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {currentQuestion.question_text}
                    </Typography>

                    {currentQuestion.image && currentQuestion.image.url && (
                        <ImageViewer
                            src={getImageUrl(currentQuestion.image.url)}
                            alt={currentQuestion.image.alt_text || 'Question Diagram'}
                        />
                    )}

                    <Grid container spacing={2} direction="column" sx={{ mb: 4 }}>
                        {answers.map((value, index) => {
                            const isCorrectAnswer = ['A', 'B', 'C', 'D'][index] === currentQuestion.correct_answer;
                            const isSelected = selectedAnswer === index;
                            const showAsCorrect =
                                (isCorrectAnswer && selectedAnswer !== null && !correctAnswer) || (isSelected && correctAnswer);

                            return (
                                <Grid item xs={12} key={index}>
                                    <Card
                                        onClick={() => handleAnswerSelection(index)}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: isSelected
                                                ? showAsCorrect
                                                    ? 'success.main'
                                                    : 'error.main'
                                                : selectedAnswer !== null && isCorrectAnswer
                                                  ? 'success.main'
                                                  : 'background.paper',
                                            color: isSelected || (selectedAnswer !== null && isCorrectAnswer) ? 'white' : 'text.primary',
                                            transition: 'transform 0.3s ease-in-out, background-color 0.3s',
                                            '&:hover': {
                                                transform: 'scale(1.02)', // Reduced scale effect
                                                backgroundColor: selectedAnswer === null ? '#ff5e1c' : '',
                                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                                            },
                                            borderRadius: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <CardActionArea>
                                            <CardContent
                                                sx={{
                                                    py: 2,
                                                    px: 3, // Increased horizontal padding
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    align="left" // Changed to left alignment
                                                    sx={{
                                                        fontFamily: '"Poppins", sans-serif',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        '& .option-letter': {
                                                            minWidth: '30px', // Ensures consistent spacing
                                                            fontWeight: 600,
                                                            marginRight: 1,
                                                        },
                                                    }}
                                                >
                                                    <span className="option-letter">{['A', 'B', 'C', 'D'][index]}.</span>
                                                    <span>{value}</span>
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {selectedAnswer !== null && (
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            {correctAnswer ? (
                                <Typography variant="h6" color="success.main">
                                    <CheckCircleIcon sx={{ verticalAlign: 'middle' }} /> Correct!
                                </Typography>
                            ) : (
                                <Typography variant="h6" color="error.main">
                                    <CancelIcon sx={{ verticalAlign: 'middle' }} /> Incorrect!
                                </Typography>
                            )}
                        </Box>
                    )}
                    {renderReferenceAndSolution()}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            position: 'relative',
                            zIndex: 10,
                            mt: 4,
                            gap: 2,
                        }}
                    >
                        <Button
                            variant="outlined"
                            sx={{
                                borderColor: '#ff5e1c',
                                color: '#ff5e1c',
                                '&:hover': {
                                    backgroundColor: '#ff5e1c',
                                    color: 'white',
                                },
                                px: 4,
                                boxShadow: '0 4px 8px rgba(255, 94, 28, 0.3)',
                            }}
                            disabled={currentQuestionIndex === 0}
                            onClick={handlePreviousQuestion}
                        >
                            Previous
                        </Button>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#ff5e1c',
                                    '&:hover': { backgroundColor: '#e65419' },
                                    px: 4,
                                    boxShadow: '0 4px 12px rgba(255, 94, 28, 0.4)',
                                }}
                                onClick={handleAIAssitant}
                            >
                                Ask Our AI
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#ff5e1c',
                                    '&:hover': { backgroundColor: '#e65419' },
                                    px: 4,
                                    boxShadow: '0 4px 12px rgba(255, 94, 28, 0.4)',
                                    // Add styles for disabled state
                                    '&.Mui-disabled': {
                                        backgroundColor: 'rgba(255, 94, 28, 0.3)',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    },
                                }}
                                onClick={handleNextQuestion}
                                disabled={selectedAnswer === null} // Disable button if no answer is selected
                            >
                                {currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next'}
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 4,
                        }}
                    >
                        <Button variant="outlined" color="secondary" sx={{ mx: 2 }} onClick={handleResetTest}>
                            Reset Test
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleRetakeMissedQuestions}
                            sx={{ mx: 2 }}
                            disabled={missedQuestions.length === 0}
                        >
                            Retake Missed Questions ({missedQuestions.length})
                        </Button>
                        {isRetakingMissed && (
                            <Button variant="outlined" color="primary" onClick={handleResumeFullTest} sx={{ mx: 2 }}>
                                Resume Full Test
                            </Button>
                        )}
                    </Box>
                </Box>
            </Fade>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Test Completed</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have completed the test. Your score is {score} correct and {incorrectAnswers} incorrect. You missed{' '}
                        {missedQuestions.length} questions.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                    {missedQuestions.length > 0 && (
                        <Button onClick={handleRetakeMissedQuestions} color="primary">
                            Retake Missed Questions
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <ChatBot toggleWebchat={handleAIAssitant} chatBotOpen={isWebchatOpen} />
        </Container>
    );
};

export default TestPage;
